import { DocumentMyDeskResponse } from "apis/data-contracts";
import { modalStore } from "components/Common/Alert/AlertManager";
import { Table } from "components/Common/Table/Table";
import { DOCUMENT_CATEGORY_UUID } from "config/constant";
import { EDITABLE_DOCUMENT_STATUS_LIST } from "config/documentConstant";
import { ROUTER_PATH } from "config/path";
import useMediaView from "features/common/hooks/useMediaView";
import useCategoryColumnsQuery from "features/document/hooks/queries/useCategoryColumnsQuery";
import useDocumentCategoriesQuery from "features/document/hooks/queries/useDocumentCategoriesQuery";
// import useUserDetailQuery from "features/user/hooks/queries/useUserDetailQuery";
import React from "react";
import { useNavigate } from "react-router-dom";
import openPdfPreview from "utils/openPdfPreview";

interface DeskTableProps {
  categoryId: string;
  data: DocumentMyDeskResponse[];
  page: number;
  totalData: number;
}

const STATUS_TEXT_MAP = {
  OWNER: {
    TEMP: "임시저장",
    APPROVE_IN_PROGRESS: "결재대기",
    ANALYSIS_APPROVE_IN_PROGRESS: "2단계 결재대기",
    REQ_SUPPLEMENT: "보완필요",
    APPROVE: "결재완료",
    ARCHIVES_APPROVE: "등록완료",
    REVIEW_IN_PROGRESS: "1단계 결재대기",
    REVIEW: "1단계 결재완료",
  },
  APPROVER: {
    TEMP: "임시저장",
    APPROVE_IN_PROGRESS: "결재요청",
    ANALYSIS_APPROVE_IN_PROGRESS: "2단계 결재요청",
    REQ_SUPPLEMENT: "보완요청",
    APPROVE: "결재완료",
    ARCHIVES_APPROVE: "등록완료",
    REVIEW_IN_PROGRESS: "1단계 결재요청",
    REVIEW: "1단계 결재완료",
  },
  REFERRER: {
    TEMP: "-",
    APPROVE_IN_PROGRESS: "참조권한",
    REQ_SUPPLEMENT: "-",
    APPROVE: "결재완료",
    REVIEW_IN_PROGRESS: "참조권한",
    REVIEW: "1단계 결재완료",
  },
  READER: {
    TEMP: "-",
    APPROVE_IN_PROGRESS: "-",
    REQ_SUPPLEMENT: "-",
    APPROVE: "결재완료",
    ARCHIVES_APPROVE: "등록완료",
    REVIEW_IN_PROGRESS: "-",
    REVIEW: "1단계 결재완료",
  },
} as const;

const DeskTable = ({ categoryId, data, page, totalData }: DeskTableProps) => {
  const isSmall = useMediaView("small");

  const navigate = useNavigate();

  const { data: columnsData } = useCategoryColumnsQuery({
    categoryUuid: categoryId,
    listType: "MY_DESK",
    isMobile: isSmall,
  });
  const { data: categoryData } = useDocumentCategoriesQuery();

  const columns = columnsData.data.result;

  const handleArchivesRowClick = (
    uuid: string,
    status: DocumentMyDeskResponse["status"],
    documentFileUrl: string,
    documentTitle?: string,
  ) => {
    if (status === "TEMP") {
      if (isSmall) {
        modalStore.register({
          id: "archives-warn-modal",
          title: "등록/수정 불가",
          content: "문서 등록은 WEB에서만 가능합니다.",
        });
      } else {
        navigate(`/document/archives/create/${uuid}`);
      }
    } else {
      void openPdfPreview(documentFileUrl, documentTitle);
    }
  };

  const handleWorkRowClick = (
    id: string,
    documentName: string,
    status: DocumentMyDeskResponse["status"],
    isOwner: boolean,
  ) => {
    if (isOwner && EDITABLE_DOCUMENT_STATUS_LIST.includes(status)) {
      if (documentName !== "휴가신청서" && isSmall) {
        modalStore.register({
          id: "work-warn-modal",
          title: "등록/수정 불가",
          content: "문서 등록은 WEB에서만 가능합니다.",
        });
      } else {
        navigate(`/document/work/create/${id}`);
      }
    } else {
      navigate(`/document/work/${id}`);
    }
  };

  const handleAnalysisRowClick = (
    id: string,
    status: DocumentMyDeskResponse["status"],
    isOwner: boolean,
  ) => {
    if (isOwner && EDITABLE_DOCUMENT_STATUS_LIST.includes(status)) {
      if (isSmall) {
        modalStore.register({
          id: "analysis-warn-modal",
          title: "등록/수정 불가",
          content: "문서 등록은 WEB에서만 가능합니다.",
        });
      } else {
        navigate(ROUTER_PATH.document.analysis.create.form(id));
      }
    } else {
      navigate(`/document/analysis/${id}`);
    }
  };

  const columnsWidth = {
    [DOCUMENT_CATEGORY_UUID.WORK]: "0.5fr 0.5fr 0.5fr 1fr 0.5fr 0.5fr 0.5fr",
    [DOCUMENT_CATEGORY_UUID.ANALYSIS]:
      "0.5fr 1fr 0.5fr 0.5fr 0.5fr 0.5fr 0.5fr",
    [DOCUMENT_CATEGORY_UUID.ARCHIVES]: "0.5fr 1fr 0.5fr 0.5fr 0.5fr",
  }[categoryId];

  return (
    <Table.Table
      columns={isSmall ? "auto ".repeat(columns.length) : columnsWidth}
      width="100%"
    >
      <Table.Thead>
        <Table.Tr>
          {columns.map((column) => (
            <Table.Th key={column.keyName}>{column.name}</Table.Th>
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {data.map((row, index) => {
          const category = categoryData.data.result.find(
            (category) => category.uuid === categoryId,
          );
          const categorySlug = category?.name.toLowerCase();

          return (
            <Table.Tr
              key={row.uuid}
              onClick={() => {
                if (categorySlug === "archives") {
                  handleArchivesRowClick(
                    row.uuid,
                    row.status,
                    row.documentFileUrl,
                    row.name,
                  );
                } else if (categorySlug === "work") {
                  handleWorkRowClick(
                    row.uuid,
                    row.name,
                    row.status,
                    row.permissionType === "OWNER",
                  );
                } else if (categorySlug === "analysis") {
                  handleAnalysisRowClick(
                    row.uuid,
                    row.status,
                    row.permissionType === "OWNER",
                  );
                }
              }}
            >
              {columns.map((column) => {
                let field = row.additionalFields[
                  column.keyName
                ] as unknown as string;
                const fieldName = row.name;

                if (column.keyName === "documentStatus") {
                  field =
                    STATUS_TEXT_MAP[row.permissionType ?? "REFERRER"][
                      field as DocumentMyDeskResponse["status"]
                    ];

                  if (
                    categorySlug === "analysis" &&
                    row.permissionType === "OWNER" &&
                    row.status === "APPROVE_IN_PROGRESS"
                  ) {
                    field = STATUS_TEXT_MAP.OWNER.ANALYSIS_APPROVE_IN_PROGRESS;
                  }

                  if (
                    categorySlug === "analysis" &&
                    row.permissionType === "APPROVER" &&
                    row.status === "APPROVE_IN_PROGRESS"
                  ) {
                    field =
                      STATUS_TEXT_MAP.APPROVER.ANALYSIS_APPROVE_IN_PROGRESS;
                  }

                  if (row.status === "APPROVE" && categorySlug === "archives") {
                    field = STATUS_TEXT_MAP.OWNER.ARCHIVES_APPROVE;
                  }
                }

                // documentCategoryNo가 아닌 웹에서 번호 순서대로 작동해야함
                if (column.keyName === "documentCategoryNo") {
                  field = (totalData - (index + (page - 1) * 10)).toString();
                }

                if (column.keyName === "name") {
                  field = fieldName;
                }

                return (
                  <Table.Td
                    key={row.uuid + field + column.keyName}
                    attention={
                      column.keyName === "documentStatus" &&
                      row.status !== "APPROVE" &&
                      row.status !== "REVIEW" &&
                      row.status !== "TEMP" &&
                      row.permissionType !== "REFERRER" &&
                      row.permissionType !== "READER"
                    }
                  >
                    {field}
                  </Table.Td>
                );
              })}
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table.Table>
  );
};

export default DeskTable;
